import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Loader2, AlertCircle, ShoppingBag, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";

function AuthShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#F2E6E7] blur-3xl opacity-70" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#E8CFD1] blur-3xl opacity-50" />
      <div className="relative w-full max-w-md">{children}</div>
    </div>
  );
}

function Field({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
      <input
        {...props}
        className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-[#E8CFD1] text-[#2C2A29] placeholder:text-[#B8B2AD] outline-none focus:border-[#C58B90] focus:ring-2 focus:ring-[#F2E6E7] transition-[border-color,box-shadow]"
      />
    </div>
  );
}

function PasswordField(props) {
  const [show, setShow] = useState(false);
  const tid = props["data-testid"];
  return (
    <div className="relative">
      <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
      <input
        {...props}
        type={show ? "text" : "password"}
        className="w-full h-12 pl-11 pr-12 rounded-2xl bg-white border border-[#E8CFD1] text-[#2C2A29] placeholder:text-[#B8B2AD] outline-none focus:border-[#C58B90] focus:ring-2 focus:ring-[#F2E6E7] transition-[border-color,box-shadow]"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${tid}-toggle`}
        aria-label={show ? "Ocultar senha" : "Mostrar senha"}
        title={show ? "Ocultar senha" : "Mostrar senha"}
        className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full flex items-center justify-center text-[#C58B90] hover:bg-[#F7EBEC] transition-colors"
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      login(res.data.token, res.data.user);
      toast.success(`Bem-vinda de volta, ${res.data.user.name.split(" ")[0]}! 💗`);
      navigate("/");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="bg-white rounded-[2rem] p-8 sm:p-10 border border-[#F2E6E7] bc-shadow-hover animate-float-up">
        <div className="flex flex-col items-center text-center mb-8">
          <Logo size={64} />
          <h1 className="font-serif text-3xl mt-5 text-[#2C2A29]">Brenda Club</h1>
          <p className="text-sm text-[#7A7571] mt-1">Sua biblioteca exclusiva de stickers 💗</p>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="mb-5 flex items-start gap-2 rounded-2xl bg-[#FBEDEE] border border-[#E8CFD1] px-4 py-3 text-sm text-[#A05055]"
          >
            <AlertCircle size={17} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <Field
            icon={Mail}
            type="email"
            placeholder="Seu email"
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <PasswordField
            placeholder="Sua senha"
            data-testid="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-12 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 transition-[background-color,transform] hover:bg-[#A87277] active:scale-[0.99] disabled:opacity-70"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            Entrar
          </button>
        </form>

        <div className="text-center mt-4">
          <Link to="/esqueci-senha" data-testid="forgot-link" className="text-sm text-[#C58B90] font-medium">
            Esqueci minha senha
          </Link>
        </div>

        <div className="my-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-[#F2E6E7]" />
          <span className="text-xs text-[#B8B2AD] uppercase tracking-widest">ou</span>
          <span className="h-px flex-1 bg-[#F2E6E7]" />
        </div>

        <Link
          to="/primeiro-acesso"
          data-testid="kiwify-first-access-btn"
          className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#F2E6E7] text-[#8A5F63] font-semibold transition-colors hover:bg-[#E8CFD1]"
        >
          <ShoppingBag size={17} /> Comprei na Kiwify — Primeiro acesso
        </Link>

        <p className="text-center text-sm text-[#7A7571] mt-6">
          Ainda não tem conta?{" "}
          <Link to="/cadastro" data-testid="go-register" className="text-[#C58B90] font-semibold">
            Cadastre-se
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const res = await api.post("/auth/register", { name, email, password });
      setSuccess(res.data.message);
      toast.success("Cadastro enviado! 💗");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="bg-white rounded-[2rem] p-8 sm:p-10 border border-[#F2E6E7] bc-shadow-hover animate-float-up">
        <div className="flex flex-col items-center text-center mb-8">
          <Logo size={64} />
          <h1 className="font-serif text-3xl mt-5 text-[#2C2A29]">Criar conta</h1>
          <p className="text-sm text-[#7A7571] mt-1">Faça parte do clube 🎀</p>
        </div>

        {error && (
          <div
            data-testid="register-error"
            className="mb-5 flex items-start gap-2 rounded-2xl bg-[#FBEDEE] border border-[#E8CFD1] px-4 py-3 text-sm text-[#A05055]"
          >
            <AlertCircle size={17} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success ? (
          <div data-testid="register-success" className="text-center">
            <div className="rounded-2xl bg-[#F2E6E7] px-5 py-6 text-[#8A5F63] leading-relaxed">{success}</div>
            <button
              onClick={() => navigate("/login")}
              data-testid="register-go-login"
              className="mt-6 w-full h-12 rounded-full bg-[#C58B90] text-white font-semibold hover:bg-[#A87277] transition-colors"
            >
              Ir para o login
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="space-y-4">
              <Field
                icon={Mail}
                type="text"
                placeholder="Seu nome"
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <Field
                icon={Mail}
                type="email"
                placeholder="Seu email"
                data-testid="register-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Field
                icon={Lock}
                type="password"
                placeholder="Crie uma senha (mín. 6 caracteres)"
                data-testid="register-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="register-submit"
                className="w-full h-12 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 transition-[background-color,transform] hover:bg-[#A87277] active:scale-[0.99] disabled:opacity-70"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                Cadastrar
              </button>
            </form>
            <p className="text-center text-sm text-[#7A7571] mt-6">
              Já tem conta?{" "}
              <Link to="/login" data-testid="go-login" className="text-[#C58B90] font-semibold">
                Entrar
              </Link>
            </p>
          </>
        )}
      </div>
    </AuthShell>
  );
}
